/* eslint-disable no-fallthrough */
import { combine, createEffect, createEvent, createStore } from 'effector';
import { get } from 'lodash';
import update from 'immutability-helper';
import moment, { Moment } from 'moment';
import api from '@constants/endpoints';
import { IPromotionActivity, IPromotionActivityAction } from '@interfaces/IPromotionActivity';
import request from '@helpers/request';
import createPaginationStore from '@stores/pagination';
import createFiltersStore from './filters';
import createPreloadersStore from './preloaders';
import {
  mapDataToFieldsValue,
  mapValueToRequest,
} from '@pages/PromotionPage/Activity/PromotionActivityEditModal/PromotionActivityEditModal.helpers';
import localeWeight, { LOCALE } from './localeWeight';

export const reset = createEvent('PromotionActivity store reset');

interface dateProperties {
  from: string;
  to: string;
}

const defaultFilters = {
  statusFilter: 'active',
  afterFilter: undefined,
  beforeFilter: undefined,
};

export const getList = createEffect({
  name: 'getList',
  async handler() {
    const { current, ...other } = pagination.getState();
    const { afterFilter, beforeFilter, statusFilter } = filters.getState();

    const compare = (operation: string, dateOne: Moment | null, dateTwo: Moment | null) => {
      if (!dateOne) {
        return dateTwo;
      }
      if (!dateTwo) {
        return dateOne;
      }
      if (operation === 'max') {
        return dateOne.isBefore(dateTwo) ? dateTwo : dateOne;
      }
      if (operation === 'min') {
        return dateOne.isBefore(dateTwo) ? dateOne : dateTwo;
      }
    };

    const timeLineFilter = (status: string, range: dateProperties) => {
      const { from, to } = range;
      const activeDate = status === 'active' ? moment() : null;
      return {
        startsBeforeFilter: compare('min', activeDate, to ? moment(to) : null)?.format(),
        startsAfterFilter: status === 'planned' ? moment().format() : null,
        endsBeforeFilter: status === 'ended' ? moment().format() : null,
        endsAfterFilter: compare('max', activeDate, from ? moment(from) : null)?.format(),
      };
    };

    const data = {
      localeFilter: localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState(),
      page: {
        type: 'PAGE',
        ...other,
        page: (current || 1) - 1,
      },
      ...timeLineFilter(statusFilter, { from: afterFilter, to: beforeFilter }),
    };

    const response = await request(api.activity.getList, { data });
    return response;
  },
});

const updateListHandler = (
  state: IPromotionActivity[],
  {
    result: {
      data: {
        body: { promo },
      },
    },
    params: { id },
  }: { params: { id?: number }; result: { data: { body: { promo: IPromotionActivity } } } },
) => {
  if (id) {
    const foundIndex = state.findIndex((item) => item.id === id);
    if (foundIndex !== -1) {
      const updatedState: any = update(state, { [foundIndex]: { $set: promo } });
      return updatedState;
    }
  } else {
    getList();
  }
};


export const createPromotionActivity = createEffect({
  name: 'createPromotionActivity',
  async handler(data: IPromotionActivityAction) {
    const response = await request(api.activity.create, { data });
    return response;
  },
});


export const editPromotionActivity = createEffect({
  name: 'editPromotionActivity',
  async handler(data: IPromotionActivityAction) {
    const response = await request(api.activity.edit, {
      data,
      urlParams: { promoId: data.id?.toString() },
    });
    return response;
  },
});

export const finishPromotionActivity = createEffect({
  name: 'finishPromotionActivity',
  async handler(value: IPromotionActivity) {
    const data = mapValueToRequest(mapDataToFieldsValue(value));
    const response = await request(api.activity.edit, {
      data: {
        ...data,
        ends: moment().subtract(1, 'hours').format(),
      },
      urlParams: { promoId: data.id?.toString() },
    });
    return response;
  },
});

const preloaders = createPreloadersStore([createPromotionActivity, editPromotionActivity, getList]);

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'activity',
  resetEvent: reset,
  bodyPath: ['data', 'body', 'page'],
});

export { setPagination };

const { filters, setFilters } = createFiltersStore({
  listName: 'activity',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const list = createStore<IPromotionActivity[]>([])
  .on(getList.done, (state, { result }) => get(result, ['data', 'body', 'page', 'page'], []))
  .on(createPromotionActivity.done, updateListHandler)
  .on(editPromotionActivity.done, updateListHandler)
  .on(finishPromotionActivity.done, () => {
    updateListHandler;
    getList();
  })
  .reset(reset);

const activity = combine({ list, preloaders, pagination, filters, setFilters, setPagination });

export default activity;
